// file: src/pages/AllBookings.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getAllLandlordReservations } from "../api/landlordApi";

/* Brand */
const BLUE = "#2A7EFA";
const BLUE_LIGHT = "#5AA9FF";

const clsx = (...p) => p.filter(Boolean).join(" ");

/* --- Utilities --- */
function normalize(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/* --- VERCEL-SAFE ISO PARSER --- */
function parseIsoSafe(s) {
  if (!s) return null;
  if (s instanceof Date) return Number.isNaN(s.getTime()) ? null : s;
  if (typeof s === "number") {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  let str = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(str))
    str = str.replace(" ", "T");
  if (/[zZ]$/.test(str) || /[-+]\d{2}:\d{2}$/.test(str)) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(str)) str = str + ":00Z";
  else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(str)) str = str + "Z";
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtPartsTZ(iso, timeZone) {
  try {
    const d = parseIsoSafe(iso);
    if (!d) return { date: iso, time: "" };
    return {
      date: d.toLocaleDateString(undefined, {
        timeZone: timeZone || undefined,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
      time: d.toLocaleTimeString(undefined, {
        timeZone: timeZone || undefined,
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  } catch {
    return { date: iso, time: "" };
  }
}

function platePretty(p) {
  return String(p || "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
}

/* --- Tiny atoms --- */
function StatusBadge({ status = "valid" }) {
  const map = {
    active: {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      dot: "bg-emerald-500",
    },
    valid: {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      dot: "bg-emerald-500",
    },
    cancelled: { bg: "bg-rose-50", text: "text-rose-700", dot: "bg-rose-500" },
    expired: { bg: "bg-slate-50", text: "text-slate-600", dot: "bg-slate-400" },
    pending: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  };
  const s = map[status] || map.valid;
  const label = String(status || "valid");
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] sm:text-xs font-semibold leading-none",
        s.bg,
        s.text
      )}
    >
      <span className={clsx("h-2 w-2 rounded-full", s.dot)} />
      {label[0].toUpperCase() + label.slice(1)}
    </span>
  );
}

function TypeChip({ type }) {
  if (!type) return null;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-1 text-[10px] sm:text-[11px] font-semibold leading-none bg-sky-50 text-sky-700 ring-1 ring-sky-100"
      title={type}
    >
      {type}
    </span>
  );
}

function PlatePill({ plate }) {
  if (!plate) return null;
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-white shadow whitespace-nowrap"
      style={{ background: BLUE, boxShadow: `0 8px 20px ${BLUE}22` }}
      title={plate}
      aria-label={`License plate ${plate}`}
    >
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        aria-hidden
      >
        <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
        <path
          d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
          fill="currentColor"
        />
      </svg>
      <span className="font-mono tracking-wider text-[12px] sm:text-[13px]">
        {platePretty(plate)}
      </span>
    </div>
  );
}

/* --- Booking card --- */
function BookingCard({ booking }) {
  const start = fmtPartsTZ(booking.startsAt, booking.tz);
  const end = fmtPartsTZ(booking.endsAt, booking.tz);

  return (
    <article
      className="relative rounded-2xl border border-black/[0.06] bg-white/80 backdrop-blur p-4 sm:p-5 shadow-sm transition will-change-transform hover:-translate-y-0.5 hover:shadow-md overflow-hidden"
      style={{
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,.8), 0 14px 30px rgba(0,0,0,.04)",
      }}
    >
      <div
        className="pointer-events-none absolute -top-6 -right-6 h-16 w-16 rounded-full blur-2xl"
        style={{ background: `${BLUE}1f` }}
        aria-hidden
      />
      <div className="flex flex-wrap items-center sm:justify-between justify-start gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-xl text-white shadow shrink-0"
            style={{
              background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
              boxShadow: `0 10px 22px ${BLUE}22`,
            }}
            aria-hidden
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4.5 w-4.5 sm:h-5 sm:w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
              <path
                d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
                fill="currentColor"
              />
            </svg>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
              Facility
            </div>
            <div className="truncate text-sm sm:text-base font-semibold leading-6 tracking-tight text-slate-800">
              {booking.facilityName}
            </div>
          </div>
        </div>
        <div className="shrink-0 flex flex-wrap items-center gap-2">
          <TypeChip type={booking.type} />
          <StatusBadge status={booking.status} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[1fr,1fr,240px]">
        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0">
          <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
            Start
          </div>
          <div className="mt-1 text-sm font-medium leading-5 text-slate-800">
            {start.date}
          </div>
          <div className="text-sm leading-5 text-slate-700">{start.time}</div>
        </div>
        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0">
          <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
            End
          </div>
          <div className="mt-1 text-sm font-medium leading-5 text-slate-800">
            {end.date}
          </div>
          <div className="text-sm leading-5 text-slate-700">{end.time}</div>
        </div>
        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0 overflow-hidden sm:col-span-2 xl:col-span-1">
          <div className="mb-2 text-[11px] sm:text-xs leading-4 text-slate-500">
            License Plate
          </div>
          <PlatePill plate={booking.licensePlate} />
        </div>
      </div>
    </article>
  );
}

/* --- Helpers: today bounds in LOCAL TZ + time getters --- */
function ts(d) {
  const parsed = parseIsoSafe(d);
  if (!parsed) return NaN;
  return parsed.getTime();
}
function todayStartEndLocal() {
  const now = new Date();
  const start = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0
  ).getTime();
  const end = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
    999
  ).getTime();
  return [start, end];
}

/* --- Pagination helpers --- */
function range(a, b) {
  const out = [];
  for (let i = a; i <= b; i++) out.push(i);
  return out;
}
function getPageNumbers(page, pageCount, maxButtons = 5) {
  if (pageCount <= maxButtons + 2) return range(1, pageCount);
  const side = Math.floor(maxButtons / 2);
  let start = Math.max(2, page - side);
  let end = Math.min(pageCount - 1, page + side);
  const missing = maxButtons - (end - start + 1);
  if (missing > 0) {
    if (start === 2) end = Math.min(pageCount - 1, end + missing);
    else if (end === pageCount - 1) start = Math.max(2, start - missing);
  }
  return [
    1,
    ...(start > 2 ? ["…"] : []),
    ...range(start, end),
    ...(end < pageCount - 1 ? ["…"] : []),
    pageCount,
  ];
}
function Pager({ page, pageCount, onPage }) {
  if (pageCount <= 1) return null;
  const nums = getPageNumbers(page, pageCount, 5);
  const btnBase =
    "rounded-lg border border-black/[0.06] px-3 py-2 text-sm transition disabled:opacity-50";
  const solidBtn =
    "rounded-lg px-3 py-2 text-sm font-semibold text-white shadow";
  const solidStyle = {
    background: `linear-gradient(135deg, ${BLUE}, ${BLUE})`,
    boxShadow: `0 10px 22px ${BLUE}22`,
  };
  return (
    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 sm:gap-2">
      <div className="text-sm text-black/60">
        Page <span className="font-medium text-black">{page}</span> of{" "}
        <span className="font-medium text-black">{pageCount}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => onPage(1)}
          className={btnBase}
          disabled={page === 1}
          aria-label="First page"
        >
          « First
        </button>
        <button
          onClick={() => onPage(Math.max(1, page - 1))}
          className={btnBase}
          disabled={page === 1}
          aria-label="Previous page"
        >
          Prev
        </button>
        <div className="flex items-center gap-1">
          {nums.map((n, i) =>
            n === "…" ? (
              <span key={`e-${i}`} className="px-2 text-sm text-slate-500">
                …
              </span>
            ) : (
              <button
                key={n}
                onClick={() => onPage(n)}
                aria-current={n === page ? "page" : undefined}
                className={
                  n === page
                    ? `${solidBtn}`
                    : `${btnBase} bg-white hover:bg-slate-50`
                }
                style={n === page ? solidStyle : undefined}
              >
                {n}
              </button>
            )
          )}
        </div>
        <button
          onClick={() => onPage(Math.min(pageCount, page + 1))}
          className={btnBase}
          disabled={page === pageCount}
          aria-label="Next page"
        >
          Next
        </button>
        <button
          onClick={() => onPage(pageCount)}
          className={btnBase}
          disabled={page === pageCount}
          aria-label="Last page"
        >
          Last »
        </button>
      </div>
    </div>
  );
}

/* --- Page (today-only) --- */
export default function AllBookings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("valid");
  const pageSize = 10;

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const body = await getAllLandlordReservations(
        { limit: 1000, offset: 0, statuses: status },
        { timeout: 20000 }
      );

      const list = Array.isArray(body)
        ? body
        : body?.results ||
          body?.items ||
          body?.reservations ||
          body?.reservation?.results ||
          [];

      const mapped = list.map((r, i) => {
        const startRaw =
          r.starts ||
          r.start ||
          r.start_time ||
          r.starts_at ||
          r.start_time_utc;
        const endRaw =
          r.ends || r.end || r.end_time || r.ends_at || r.end_time_utc;
        const startD = parseIsoSafe(startRaw);
        const endD = parseIsoSafe(endRaw);

        return {
          id: r.reservation_id ?? r.display_id ?? `row-${i}`,
          facilityName:
            r.facility_title || r.facility_name || r.facility || "Facility",
          licensePlate: r.license_plate || r.plate || "",
          startsAt: startD ? startD.toISOString() : startRaw,
          endsAt: endD ? endD.toISOString() : endRaw,
          status: (r.status || "valid").toLowerCase(),
          tz: r.facility_timezone || r.timezone || undefined,
          type: r.type || "",
        };
      });

      setRows(mapped);
      setPage(1);
    } catch (e) {
      setErr(e?.payload?.message || e?.message || "Failed to load bookings.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [status]);

  // today-only filter + text filter + default sort (start asc), then paginate
  const filteredSorted = useMemo(() => {
    const [todayStart, todayEnd] = todayStartEndLocal();
    const needle = normalize(q);

    let base = rows.filter((b) => {
      // text filter
      const okText =
        !needle ||
        normalize(b.facilityName).includes(needle) ||
        platePretty(b.licensePlate).includes(needle.toUpperCase());

      if (!okText) return false;

      // today overlap (local)
      const s = ts(b.startsAt);
      const e = ts(b.endsAt);
      if (Number.isNaN(s) || Number.isNaN(e)) return false;
      return s <= todayEnd && e >= todayStart;
    });

    // sort by start ascending
    base.sort((a, b) => (ts(a.startsAt) || 0) - (ts(b.startsAt) || 0));
    return base;
  }, [rows, q]);

  // pagination
  const pageCount = Math.max(1, Math.ceil(filteredSorted.length / pageSize));
  const start = (page - 1) * pageSize;
  const visible = filteredSorted.slice(start, start + pageSize);

  // reset to first page when filters change
  useEffect(() => setPage(1), [q]);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-3 sm:px-4 lg:px-6 py-4 md:py-6 overflow-x-hidden">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-[20px] sm:text-[22px] lg:text-2xl font-semibold tracking-tight text-slate-900">
          All Bookings — Today
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="min-h-10 rounded-xl border border-black/[0.08] bg-white px-3 py-2 text-sm"
            title="Reservation status"
          >
            <option value="valid">Valid</option>
            <option value="cancelled">Cancelled</option>
            <option value="valid,cancelled">Valid + Cancelled</option>
          </select>
          <button
            type="button"
            onClick={load}
            className="min-h-10 rounded-xl px-3 py-2 text-sm font-semibold text-white shadow"
            style={{
              background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
              boxShadow: `0 10px 22px ${BLUE}22`,
            }}
          >
            Refresh
          </button>
        </div>
      </header>

      <section className="rounded-2xl border border-black/[0.06] bg-white/70 backdrop-blur px-3 sm:px-4 py-3 shadow-sm">
        {/* Search row (kept) */}
        <div className="flex flex-col items-stretch gap-2 sm:flex-row">
          <div className="relative flex-1 min-w-0">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by plate or facility…"
              className="w-full rounded-xl border border-black/[0.06] bg-white px-4 py-3 text-[15px] leading-5 text-slate-800 outline-none transition focus:ring-4"
              style={{
                ["--blue"]: BLUE,
                boxShadow: "inset 0 0 0 9999px transparent",
                borderColor: "rgba(0,0,0,.06)",
              }}
            />
            <div className="pointer-events-none absolute inset-y-0 right-3 grid place-items-center text-slate-400">
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3.5-3.5" />
              </svg>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setQ("")}
            className="rounded-xl border border-black/[0.08] bg-white px-3 py-2 text-xs sm:text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Clear
          </button>
        </div>

        <div className="mt-2 text-sm text-slate-600">
          {loading ? (
            "Loading…"
          ) : err ? (
            <span className="text-rose-700">{err}</span>
          ) : (
            <>
              Showing{" "}
              <span className="font-medium text-slate-900">
                {visible.length}
              </span>{" "}
              of{" "}
              <span className="font-medium text-slate-900">
                {filteredSorted.length}
              </span>{" "}
              for today
            </>
          )}
        </div>
      </section>

      {loading ? (
        <div className="grid place-items-center rounded-3xl border border-black/[0.06] bg-white p-10 sm:p-12 text-slate-600">
          Loading reservations…
        </div>
      ) : err ? (
        <div className="grid place-items-center rounded-3xl border border-rose-200 bg-rose-50 p-10 sm:p-12 text-rose-700">
          {err}
        </div>
      ) : visible.length === 0 ? (
        <div className="grid place-items-center rounded-3xl border border-black/[0.06] bg-white p-10 sm:p-12 text-slate-600">
          No bookings for today match your search.
        </div>
      ) : (
        <>
          {/* Cards — exactly 2 per row on ≥sm */}
          <section className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2">
            {visible.map((b) => (
              <BookingCard
                key={b.id || `${b.licensePlate}-${b.startsAt}`}
                booking={b}
              />
            ))}
          </section>

          <Pager page={page} pageCount={pageCount} onPage={setPage} />
        </>
      )}
    </div>
  );
}
